/**
 * CurlImportModal
 * Reusable modal: user pastes a curl command → backend parses it → onImport(parsedStep)
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onImport: (step: {method, url, headers, body}) => void
 */
import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Terminal, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_COLORS = {
  GET:    "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  POST:   "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PUT:    "bg-amber-500/20 text-amber-400 border-amber-500/30",
  PATCH:  "bg-purple-500/20 text-purple-400 border-purple-500/30",
  DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
};

export default function CurlImportModal({ open, onClose, onImport }) {
  const [curl, setCurl] = useState("");
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState("");

  const handleParse = async () => {
    if (!curl.trim()) return;
    setParsing(true);
    setError("");
    setParsed(null);
    try {
      const res = await axios.post(`${API}/imports/parse-curl`, { curl }, { withCredentials: true });
      setParsed(res.data.step);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to parse curl command");
    } finally {
      setParsing(false);
    }
  };

  const handleImport = () => {
    if (!parsed) return;
    onImport(parsed);
    setCurl("");
    setParsed(null);
    setError("");
    onClose();
    toast.success("Step imported from cURL!");
  };

  const handleClose = () => {
    setCurl("");
    setParsed(null);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0B0F1A] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Unbounded, sans-serif" }}>
            <Terminal className="w-5 h-5 text-blue-400" />
            Import from cURL
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Paste any curl command and we'll extract the method, URL, headers, and body automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Curl input */}
          <Textarea
            className="bg-black/50 border-white/10 focus:border-blue-400 text-slate-200 font-mono text-xs rounded-sm resize-none"
            rows={6}
            placeholder={`curl -X POST https://api.example.com/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"user@example.com","password":"secret"}'`}
            value={curl}
            onChange={e => { setCurl(e.target.value); setParsed(null); setError(""); }}
          />

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-sm px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Parse button */}
          {!parsed && (
            <Button
              className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-sm"
              onClick={handleParse}
              disabled={!curl.trim() || parsing}
            >
              {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Terminal className="w-4 h-4 mr-2" />}
              Parse cURL
            </Button>
          )}

          {/* Parsed preview */}
          {parsed && (
            <div className="glass-card rounded-sm border border-[#00E599]/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00E599]" />
                <span className="text-[#00E599] text-sm font-semibold">Parsed successfully</span>
              </div>

              <div className="grid grid-cols-12 gap-2 text-sm">
                <div className="col-span-2">
                  <p className="text-slate-500 text-xs mb-1">Method</p>
                  <Badge className={`font-mono text-xs border ${METHOD_COLORS[parsed.method] || METHOD_COLORS.GET}`}>
                    {parsed.method}
                  </Badge>
                </div>
                <div className="col-span-10">
                  <p className="text-slate-500 text-xs mb-1">URL</p>
                  <p className="font-mono text-xs text-slate-200 break-all">{parsed.url}</p>
                </div>
              </div>

              {Object.keys(parsed.headers || {}).length > 0 && (
                <div>
                  <p className="text-slate-500 text-xs mb-1">Headers ({Object.keys(parsed.headers).length})</p>
                  <div className="bg-black/40 rounded px-3 py-2 space-y-1">
                    {Object.entries(parsed.headers).map(([k, v]) => (
                      <div key={k} className="flex gap-2 font-mono text-xs">
                        <span className="text-blue-400 flex-shrink-0">{k}:</span>
                        <span className="text-slate-300 break-all">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {parsed.body && (
                <div>
                  <p className="text-slate-500 text-xs mb-1">Body</p>
                  <pre className="bg-black/40 rounded px-3 py-2 font-mono text-xs text-slate-300 overflow-auto max-h-32 whitespace-pre-wrap">
                    {(() => { try { return JSON.stringify(JSON.parse(parsed.body), null, 2); } catch { return parsed.body; } })()}
                  </pre>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                  onClick={handleImport}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Use This Step
                </Button>
                <Button
                  variant="ghost"
                  className="text-slate-400 hover:text-white rounded-sm"
                  onClick={() => setParsed(null)}
                >
                  Re-parse
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
